import AddIcon from '@mui/icons-material/Add';
import { Box, Grid, List, Stack, styled } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { useEffect, useState } from 'react';
import { FieldValues, FormProvider, SubmitHandler, useFormContext } from 'react-hook-form';
import { JobServeSkill } from 'wisestep-api';
import {
  WsButton,
  WsDrawer,
  WsSelect,
  ControlledTextInput,
  WsTagsList,
  ChipsType,
} from 'wisestep-base';

import CheckboxListFilter from '../../../../components/filters/components/CheckboxListFilter';
import TextInputFilter from '../../../../components/filters/components/TextInputFilter';
import WsAccordion from '../../../../components/filters/components/WsAccordion';
import BasicSearchForm from '../../types/IBasicSearchForm';
import JobServeAdvanceSearch from '../types/IJobServeAdvanceSearch';
interface AdvanceFilterProps {
  open: boolean;
  country?: string;
  onClose: () => void;
  performSearch: (values: BasicSearchForm & JobServeAdvanceSearch) => void;
  setSkills: (values: JobServeSkill[]) => void;
  skills: JobServeSkill[];
}

const DrawerFooter = styled(Grid)(() => ({
  display: 'flex',
  alignItems: 'center',
  position: 'fixed',
  bottom: 1,
  left: 2,
  right: 2,
  justifyContent: 'flex-end',
}));

const industryOptions = [
  { label: 'Construction', value: 'Construction' },
  { label: 'Customer Services', value: 'Customer Services' },
  { label: 'Energy', value: 'Energy' },
  { label: 'Engineering', value: 'Engineering' },
  { label: 'Finance', value: 'Finance' },
  { label: 'Healthcare', value: 'Healthcare' },
  { label: 'Hospitality', value: 'Hospitality' },
  { label: 'HR', value: 'HR' },
  { label: 'IT', value: 'IT' },
  { label: 'Legal', value: 'Legal' },
  { label: 'Logistics', value: 'Logistics' },
  { label: 'Media', value: 'Media' },
  { label: 'Military', value: 'Military' },
  { label: 'Office and Admin', value: 'Office and Admin' },
  { label: 'Retail', value: 'Retail' },
  { label: 'Sales and Marketing', value: 'Sales and Marketing' },
  { label: 'Science', value: 'Science' },
  { label: 'Catering', value: 'Catering' },
  { label: 'Electronics', value: 'Electronics' },
  { label: 'HR and Training', value: 'HR and Training' },
  { label: 'Rec2Rec', value: 'Rec2Rec' },
  { label: 'Tourism', value: 'Tourism' },
  { label: 'Leisure', value: 'Leisure' },
];

const employmentTypeOptions = [
  { label: 'Any', value: 'Any' },
  { label: 'Permanent', value: 'Permanent' },
  { label: 'Permanent Only', value: 'Permanent Only' },
  { label: 'Contract', value: 'Contract' },
  { label: 'Contract Only', value: 'Contract Only' },
];

const visaStatusOptions = {
  UKEur: [
    { label: 'UK/EU Citizen', value: 'UK/EU Citizen' },
    { label: 'GB Sponsorship Required', value: 'GB Sponsorship Required' },
    { label: 'Permit Holder', value: 'Permit Holder' },
  ],
  Americas: [
    { label: 'US Citizen', value: 'US Citizen' },
    { label: 'H1B Visa Holder', value: 'H1B Visa Holder' },
    { label: 'F1 Visa Holder', value: 'F1 Visa Holder' },
    { label: 'USA Sponsorship Required', value: 'USA Sponsorship Required' },
    { label: 'Green Card Holder', value: 'Green Card Holder' },
    { label: 'TN Visa Holder EAD', value: 'TN Visa Holder EAD' },
  ],
  Canada: [
    { label: 'Canadian Citizen', value: 'Canadian Citizen' },
    { label: 'CAN Sponsorship Required', value: 'CAN Sponsorship Required' },
  ],
  AusAsia: [
    { label: 'Aus/NZ Citizen', value: 'Aus/NZ Citizen' },
    { label: 'Working Visa', value: 'Working Visa' },
    { label: 'Permanent Resident', value: 'Permanent Resident' },
    { label: 'AUS Sponsorship Required', value: 'AUS Sponsorship Required' },
  ],
};

const salaryOptions = {
  UKEur: [
    { label: 'Any', value: 'Any' },
    { label: '£15,000', value: '£15,000' },
    { label: '£15,000 - £20,000', value: '£15,000 - £20,000' },
    { label: '£20,000 - £25,000', value: '£20,000 - £25,000' },
    { label: '£25,000 - £30,000', value: '£25,000 - £30,000' },
    { label: '£30,000 - £40,000', value: '£30,000 - £40,000' },
    { label: '£40,000 - £50,000', value: '£40,000 - £50,000' },
    { label: '£50,000 - £75,000', value: '£50,000 - £75,000' },
    { label: '£75,000 - £100,000', value: '£75,000 - £100,000' },
    { label: '£100,000 +', value: '£100,000 +' },
  ],
  Americas: [
    { label: 'Any', value: 'Any' },
    { label: '$22,000', value: '$22,000' },
    { label: '$22,000 - $30,000', value: '$22,000 - $30,000' },
    { label: '$30,000 - $37,000', value: '$30,000 - $37,000' },
    { label: '$37,000 - $45,000', value: '$37,000 - $45,000' },
    { label: '$45,000 - $60,000', value: '$45,000 - $60,000' },
    { label: '$60,000 - $75,000', value: '$60,000 - $75,000' },
    { label: '$75,000 - $112,000', value: '$75,000 - $112,000' },
    { label: '$112,000 - $150,000', value: '$112,000 - $150,000' },
    { label: '$150,000 +', value: '$150,000 +' },
  ],
  Canada: [
    { label: 'Any', value: 'Any' },
    { label: '$22,000', value: '$22,000' },
    { label: '$22,000 - $30,000', value: '$22,000 - $30,000' },
    { label: '$30,000 - $37,000', value: '$30,000 - $37,000' },
    { label: '$37,000 - $45,000', value: '$37,000 - $45,000' },
    { label: '$45,000 - $60,000', value: '$45,000 - $60,000' },
    { label: '$60,000 - $75,000', value: '$60,000 - $75,000' },
    { label: '$75,000 - $112,000', value: '$75,000 - $112,000' },
    { label: '$112,000 - $150,000', value: '$112,000 - $150,000' },
    { label: '$150,000 +', value: '$150,000 +' },
  ],
  AusAsia: [
    { label: 'Any', value: 'Any' },
    { label: '$30,000', value: '$30,000' },
    { label: '$30,000 - $40,000', value: '$30,000 - $40,000' },
    { label: '$30,000 - $40,000', value: '$30,000 - $40,000' },
    { label: '$40,000 - $50,000', value: '$40,000 - $50,000' },
    { label: '$50,000 - $60,000', value: '$50,000 - $60,000' },
    { label: '$60,000 - $80,000', value: '$60,000 - $80,000' },
    { label: '$80,000 - $100,000', value: '$80,000 - $100,000' },
    { label: '$100,000 - $125,000', value: '$100,000 - $125,000' },
    { label: '$125,000 - $150,000', value: '$125,000 - $150,000' },
    { label: '$150,000 +', value: '$150,000 +' },
  ],
};

const minEducationOptions = {
  UKEur: [
    { label: 'Any', value: 'Any' },
    { label: 'GCSE / O-Level', value: 'GCSE / O-Level' },
    { label: 'A Level, OND, GNVQ', value: 'A Level, OND, GNVQ' },
    { label: 'HND', value: 'HND' },
    { label: 'Bachelors Degree', value: 'Bachelors Degree' },
    { label: 'Masters Degree', value: 'Masters Degree' },
    { label: 'Doctorate, PhD', value: 'Doctorate, PhD' },
  ],
  Americas: [
    { label: 'Any', value: 'Any' },
    { label: 'High School Diploma', value: 'High School Diploma' },
    { label: 'Degree', value: 'Degree' },
    { label: 'Diploma', value: 'Diploma' },
    { label: 'Bachelors Degree', value: 'Bachelors Degree' },
    { label: 'Masters Degree', value: 'Masters Degree' },
    { label: 'Doctorate, PhD', value: 'Doctorate, PhD' },
  ],
  Canada: [
    { label: 'Any', value: 'Any' },
    { label: 'Secondary Education', value: 'Secondary Education' },
    { label: 'College Education', value: 'College Education' },
    { label: 'Diploma Education', value: 'Diploma Education' },
    { label: 'Bachelors Degree', value: 'Bachelors Degree' },
    { label: 'Masters Degree', value: 'Masters Degree' },
    { label: 'Doctorate, PhD', value: 'Doctorate, PhD' },
  ],
  AusAsia: [
    { label: 'Any', value: 'Any' },
    { label: 'Secondary School Certificate', value: 'Secondary School Certificate' },
    { label: 'HSC', value: 'HSC' },
    { label: 'Diploma Education', value: 'Diploma Education' },
    { label: 'Bachelors Degree', value: 'Bachelors Degree' },
    { label: 'Masters Degree', value: 'Masters Degree' },
    { label: 'Doctorate, PhD', value: 'Doctorate, PhD' },
  ],
};

const AdvanceFilter = ({
  open,
  onClose,
  country = 'UKEur',
  performSearch,
  setSkills,
  skills,
}: AdvanceFilterProps) => {
  const methods = useFormContext();
  const [skillsTag, setSkillsTag] = useState<string[]>([]);
  const skillName = methods.watch('skills.name');
  const skillExperience = methods.watch('skills.experience');

  const addSkill = () => {
    if (skillName && skillExperience) {
      console.log(skillName);
      console.log(skillExperience);
      setSkills([...skills, { skill: skillName, experience: skillExperience }]);
    }
  };

  const removeSkill = (skillTag: string) => {
    console.log(skillTag);
    const skillDetails = skillTag.split(':');
    const index = skills.findIndex(
      (skill) => skill.skill === skillDetails[0] && skill.experience === skillDetails[1]
    );
    skills.splice(index, 1);
    setSkills([...skills]);
  };

  useEffect(() => {
    console.log(skills);
    const tags: string[] = [];
    skills.forEach((skill) => {
      tags.push(skill.skill + ':' + skill.experience);
    });
    setSkillsTag(tags);
  }, [skills]);

  const { handleSubmit, reset } = methods;
  return (
    <FormProvider {...methods}>
      <WsDrawer
        title="Advanced Filters"
        open={open}
        onClose={() => onClose()}
        size="medium"
        headerSize="medium"
      >
        <Box
          component="form"
          role="presentation"
          onSubmit={handleSubmit(performSearch as SubmitHandler<FieldValues>)}
          onReset={() => reset()}
        >
          <Box pb={7}>
            <List>
              {/* location types */}
              <CheckboxListFilter
                name="locationTypes"
                options={{
                  Home: 'Home',
                  Preferred: 'Preferred',
                  Job: 'Job',
                  Activity: 'Activity',
                  All: 'All',
                }}
                title="Location Types"
              />

              {/* industry */}
              <WsAccordion title="Industry">
                <WsSelect
                  placeholder="Select Industry"
                  name="industry"
                  selectArray={industryOptions}
                  size="small"
                />
              </WsAccordion>

              {/* employment type */}
              <WsAccordion title="Employment Type">
                <WsSelect
                  placeholder="Select Employment Type"
                  name="employmentType"
                  selectArray={employmentTypeOptions}
                  size="small"
                />
              </WsAccordion>

              {/* salary min */}
              <WsAccordion title="Min Salary">
                <WsSelect
                  placeholder="Select Min Salary"
                  name="minSalary"
                  selectArray={salaryOptions[country]}
                  size="small"
                />
              </WsAccordion>

              {/* salary max */}
              <WsAccordion title="Max Salary">
                <WsSelect
                  placeholder="Select Max Salary"
                  name="maxSalary"
                  selectArray={salaryOptions[country]}
                  size="small"
                />
              </WsAccordion>

              {/* skills */}
              <WsAccordion title="Skills">
                <Box>
                  <Stack direction="row" spacing={2} p={2}>
                    <WsSelect
                      name="skillMatch"
                      label="Match"
                      selectArray={[
                        { label: 'Any', value: 'Any' },
                        { label: 'All', value: 'All' },
                      ]}
                    />
                    <WsSelect
                      name="skillLevel"
                      label="Level"
                      selectArray={[
                        { label: 'Min', value: 'Min' },
                        { label: 'Max', value: 'Max' },
                      ]}
                    />
                  </Stack>
                  <Stack direction="row" spacing={3} p={2}>
                    <ControlledTextInput
                      name="skills.name"
                      fullWidth
                      placeholder="Name"
                      variant="outlined"
                      size="small"
                    />
                    <WsSelect
                      name="skills.experience"
                      selectArray={[
                        { label: 'Any', value: 'Any' },
                        { label: '0 - 1 Years', value: '0 - 1 Years' },
                        { label: '1 - 3 Years', value: '1 - 3 Years' },
                        { label: '3 - 5 Years', value: '3 - 5 Years' },
                        { label: '5+ Years', value: '5+ Years' },
                        { label: 'Basic', value: 'Basic' },
                        { label: 'Intermediate', value: 'Intermediate' },
                        { label: 'Advanced', value: 'Advanced' },
                      ]}
                    />
                    <WsButton variant="contained" size="large" type="button" onClick={addSkill}>
                      <AddIcon sx={{ color: '#fff', fontSize: '1.27rem' }} />
                    </WsButton>
                  </Stack>
                  {skillsTag && !isEmpty(skillsTag) && (
                    <Stack direction="row" spacing={1} p={1}>
                      <WsTagsList
                        chipArray={skillsTag}
                        count={100}
                        type={ChipsType.PRIMARY}
                        allowDelete={true}
                        onDelete={removeSkill}
                      />
                    </Stack>
                  )}
                </Box>
              </WsAccordion>

              {/* visa status */}
              <WsAccordion title="Visa Status">
                <WsSelect
                  placeholder="Select Visa Status"
                  name="visaStatus"
                  selectArray={visaStatusOptions[country]}
                  size="small"
                />
              </WsAccordion>

              {/* security clearance */}
              <CheckboxListFilter
                name="securityClearance"
                options={{
                  'GB Can Take Clearance': 'GB Can Take Clearance',
                  'Security Check': 'Security Check',
                  'Counter Terrorism Check': 'Counter Terrorism Check',
                  'Developed Vetting': 'Developed Vetting',
                }}
                title="Security Clearance"
              />

              {/* min education */}
              <WsAccordion title="Min Education">
                <WsSelect
                  placeholder="Select Min Education"
                  name="minEducation"
                  selectArray={minEducationOptions[country]}
                  size="small"
                />
              </WsAccordion>

              {/* latest job title */}
              <TextInputFilter name="latestJobTitle " placeholder="" title="Latest Job Title " />
            </List>
          </Box>
          <DrawerFooter>
            <Stack p={1} spacing={2} direction="row">
              <WsButton variant="outlined" size="large" type="reset">
                Reset
              </WsButton>
              <WsButton variant="contained" size="large" type="submit">
                Apply
              </WsButton>
            </Stack>
          </DrawerFooter>
        </Box>
      </WsDrawer>
    </FormProvider>
  );
};

export default AdvanceFilter;
